import { get } from "lodash-es";
import { groupBy, path } from "ramda";
import { type FC, type ReactElement } from "react";

import { CardRowSimple, CardSection, ColoredLabel } from "widgets";

import { getHumanReadableAttributeValue, isMultiValue } from "../../../app-utils/sample.js";
import { generateAttributeStringCompare } from "../../../app-utils/string.js";
import { type Dictionary } from "../../../app-utils/type.js";
import {
  type AttributeValue,
  type Contig,
  type DynamicVocabularyTerm,
  type Gene,
  type Organism,
  type Parent,
  type Protein,
  type Sample,
  type Transcript,
  type Variant,
} from "../../../graphql.js";
import { ATTRIBUTE_TYPE, ENTITY_TYPE } from "../../../settings/settings.js";
import { SmartLink } from "../../core/SmartLink.js";
import { SampleTile } from "../../core/tiles/components/SampleTile.js";
import {
  ContigTile,
  GeneTile,
  GenomeTile,
  ProteinTile,
  SampleParentTile,
  TranscriptTile,
  VariantTile,
} from "../../core/tiles/index.js";
import { FileTile } from "@/modules/files/components/FileTile.js";

export interface IAttributeValuesCardRowsProps {
  values: AttributeValue[];
}

const AttributeValuesCardRows: FC<IAttributeValuesCardRowsProps> = (props) => {
  // Prepare data
  //   - fill the gaps in valueIndex for multi-value
  //   - exclude attributes without value unless it is a multi-value attribute
  //   - build groups by group name and attribute id
  //   - create the table rows
  const byAttId: Dictionary<AttributeValue[]> = groupBy(path(["attribute", "id"]), props.values ?? []);
  const stuffedRaw: AttributeValue[] = Object.keys(byAttId).flatMap((k) => {
    return fillInMissingValueIndexes(get(byAttId, k));
  });

  const rawdata: AttributeValue[] = stuffedRaw
    .filter((a) => getHumanReadableAttributeValue(a) !== "" || isMultiValue(a.attribute)) // exclude attributes having no value unless multi-value
    .sort(cmpAttributeValueByGroupNameThenByName); // reorder to group attribute having the same group name, and sort by name

  // group attribute values by groupName
  const groupedByGroupName: Dictionary<AttributeValue[]> = groupBy(path(["attribute", "groupName"]), rawdata);

  // reorder further the ungrouped attributes
  const keyOfUngroupedAttributeValues = "null";
  const grouplessAttributes = groupedByGroupName[keyOfUngroupedAttributeValues];

  if (!!grouplessAttributes && grouplessAttributes.length > 0) {
    const representing = grouplessAttributes.filter((a) => a.attribute.id < 0).sort(cmpSampleId);
    const pureAnnotational = grouplessAttributes.filter((a) => a.attribute.id >= 0);
    groupedByGroupName[keyOfUngroupedAttributeValues] = [...representing, ...pureAnnotational];
  }

  // another level of grouping by attribute id
  const groupedByGroupNameAndAttribute: Dictionary<Dictionary<AttributeValue[]>> = {};
  Object.keys(groupedByGroupName).forEach(
    (groupName: string) =>
      (groupedByGroupNameAndAttribute[groupName] = groupBy(path(["attribute", "id"]), groupedByGroupName[groupName])),
  );

  // laying out all of that
  return (
    <>
      {Object.keys(groupedByGroupNameAndAttribute).map((groupName: string) => (
        <CardSection title={groupName !== keyOfUngroupedAttributeValues ? groupName : undefined} key={groupName}>
          {Object.keys(groupedByGroupNameAndAttribute[groupName]).map((attId: string) => {
            const attributeValues: AttributeValue[] = groupedByGroupNameAndAttribute[groupName][attId];
            return <AttributeValuesCardRow key={`attr-${attId}`} attributeValues={attributeValues} />;
          })}
        </CardSection>
      ))}
    </>
  );
};

export interface IAttributeValuesCardRowProps {
  attributeValues: AttributeValue[];
}

const AttributeValuesCardRow: FC<IAttributeValuesCardRowProps> = (props) => {
  const getValues = () => {
    const attributeType = props.attributeValues[0].attribute.attributeType;
    if (attributeType.id === ATTRIBUTE_TYPE.FILE) {
      // in case of file then we show the proper file tile
      return props.attributeValues.map((attributeValue) => <FileTile file={attributeValue.projectFile} />);
    }
    // otherwise return the concatenation of all primitive values
    if (props.attributeValues.length === 1) {
      const value = props.attributeValues[0];

      // we can't use the color bullet with a tile at the moment, therefore if a color is present we enforce a normal link
      const displayValue: string | ReactElement = value.dynamicTerm?.type
        ? buildDynamicTermLink(value.dynamicTerm, !value.color)
        : getHumanReadableAttributeValue(value);

      if (value.color) {
        return <ColoredLabel color={value.color}>{displayValue}</ColoredLabel>;
      } else {
        return displayValue;
      }
    }
    if (props.attributeValues[0].dynamicTerm?.type) {
      return <>{props.attributeValues.map((v) => buildDynamicTermLink(v.dynamicTerm, true))}</>;
    } else {
      return props.attributeValues.map((v) => getHumanReadableAttributeValue(v)).join(", ");
    }
  };

  const attributeName = props.attributeValues[0].attribute.attributeName;

  return <CardRowSimple label={attributeName}>{getValues()}</CardRowSimple>;
};

// generate those only once
const cmpAttributeValueByGroupNameThenByName = generateAttributeStringCompare([
  "attribute.groupName",
  "attribute.name",
]);
const cmpSampleId = generateAttributeStringCompare("attribute.id");

function fillInMissingValueIndexes(attributeValues: AttributeValue[]): AttributeValue[] {
  const ret: AttributeValue[] = [];
  let i = 0; // last used valueIndex
  let pos = 0; // next position in attributeValues
  while (pos < attributeValues.length) {
    const curr = attributeValues[pos++];
    while (i + 1 < curr.valueIndex) {
      // assume valueIndex starts at 1
      const a: AttributeValue = {
        ...curr,
        valueIndex: i + 1,
        id: curr.id * -1,
        booleanValue: undefined,
        stringListValue: undefined,
        stringValue: undefined,
        floatValue: undefined,
        longValue: undefined,
        dateValue: undefined,
      };
      ret.push(a);
      i++;
    }
    ret.push(curr);
    i++;
  }
  return ret;
}

export const buildDynamicTermLink = (dynamicTerm: DynamicVocabularyTerm, useTile?: boolean) => {
  if (!useTile) {
    return buildSimpleDynamicTermLink(dynamicTerm);
  }
  switch (dynamicTerm.type) {
    case ENTITY_TYPE.ORGANISM:
      return <GenomeTile genome={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Organism} type={"Genome"} />;
    case ENTITY_TYPE.GENE:
      return <GeneTile gene={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Gene} />;
    case ENTITY_TYPE.CONTIG:
      return <ContigTile contig={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Contig} />;
    case ENTITY_TYPE.TRANSCRIPT:
      return <TranscriptTile transcript={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Transcript} />;
    case ENTITY_TYPE.PROTEIN:
      return <ProteinTile protein={{ id: dynamicTerm.itmId, sequenceId: dynamicTerm.name } as Protein} />;
    case ENTITY_TYPE.SAMPLE:
      return (
        <SampleTile sample={{ id: dynamicTerm.itmId, name: dynamicTerm.name, type: { name: "Sample" } } as Sample} />
      );
    case ENTITY_TYPE.SAMPLE_PARENT:
      return <SampleParentTile parent={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Parent} />;
    case ENTITY_TYPE.VARIANT:
      return <VariantTile variant={{ id: dynamicTerm.itmId, name: dynamicTerm.name } as Variant} />;
    default:
      return buildSimpleDynamicTermLink(dynamicTerm);
  }
};

const buildSimpleDynamicTermLink = (dynamicTerm: DynamicVocabularyTerm) => {
  return <SmartLink to={`/${dynamicTerm.type}/${dynamicTerm.itmId}`}>{dynamicTerm.name}</SmartLink>;
};

export default AttributeValuesCardRows;
